import React from 'react';
import { cn } from '@/utils/cn';

interface ProjectBasicInfoProps {
  form: any;
  updateForm: (data: any) => void;
  managers?: any[];
}

export function ProjectBasicInfo({ form, updateForm, managers = [] }: ProjectBasicInfoProps) {
  return (
    <section className="rounded-md bg-white p-4  border border-slate-100 space-y-4">
      <h3 className="text-sm font-normal text-slate-900 tracking-wider">Thông tin dự án</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tên dự án (Full width) */}
        <div className="space-y-1.5 md:col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-xs lg:text-sm font-normal text-slate-700">Tên dự án <span className="text-rose-500">*</span></label>
            <span className="text-xs text-slate-400 font-normal">0/150</span>
          </div>
          <input 
            required
            value={form.name || ''}
            onChange={e => updateForm({ name: e.target.value })}
            placeholder="Nhập tên dự án"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all bg-slate-50/30"
          />
        </div>

        {/* Mã dự án (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Mã dự án <span className="text-rose-500">*</span></label>
          <input 
            required
            value={form.code || ''}
            onChange={e => updateForm({ code: e.target.value })}
            placeholder="Ví dụ: PRJ-2023-001"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Loại dự án (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Loại dự án <span className="text-rose-500">*</span></label>
          <div className="flex items-center gap-6 h-10">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input 
                type="radio" 
                name="projectType" 
                checked={form.projectType === 'construction'}
                onChange={() => updateForm({ projectType: 'construction' })}
                className="hidden"
              />
              <div className={cn(
                "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                form.projectType === 'construction' ? "border-emerald-600" : "border-slate-300 group-hover:border-slate-400"
              )}>
                {form.projectType === 'construction' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />}
              </div>
              <span className="text-xs lg:text-sm font-normal text-slate-700">Thi công</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input 
                type="radio" 
                name="projectType" 
                checked={form.projectType === 'maintenance'}
                onChange={() => updateForm({ projectType: 'maintenance' })}
                className="hidden"
              />
              <div className={cn(
                "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                form.projectType === 'maintenance' ? "border-emerald-600" : "border-slate-300 group-hover:border-slate-400"
              )}>
                {form.projectType === 'maintenance' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />}
              </div>
              <span className="text-xs lg:text-sm font-normal text-slate-700">Bảo dưỡng</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer group">
              <input 
                type="radio" 
                name="projectType" 
                checked={form.projectType === 'other'}
                onChange={() => updateForm({ projectType: 'other' })}
                className="hidden"
              />
              <div className={cn(
                "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                form.projectType === 'other' ? "border-emerald-600" : "border-slate-300 group-hover:border-slate-400"
              )}>
                {form.projectType === 'other' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />}
              </div>
              <span className="text-xs lg:text-sm font-normal text-slate-700">Khác</span>
            </label>
          </div>
        </div>

        {/* Chủ đầu tư (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Chủ đầu tư (Khách hàng) <span className="text-rose-500">*</span></label>
          <input 
            required
            value={form.clientName || ''}
            onChange={e => updateForm({ clientName: e.target.value })}
            placeholder="Nhập tên chủ đầu tư"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Đơn vị quản lý (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Đơn vị quản lý / Ban quản lý</label>
          <input 
            value={form.managementUnit || ''}
            onChange={e => updateForm({ managementUnit: e.target.value })}
            placeholder="Nhập đơn vị quản lý (nếu có)"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Nhóm dự án (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Nhóm dự án</label>
          <select 
            value={form.categoryCode || ''}
            onChange={e => updateForm({ categoryCode: e.target.value })}
            className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          >
            <option value="">-- Chọn nhóm --</option>
            <option value="internal">Dự án nội bộ</option>
            <option value="external">Dự án khách hàng</option>
            <option value="experimental">Dự án thử nghiệm</option>
          </select>
        </div>

        {/* PM phụ trách chính (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">PM phụ trách</label>
          <select 
            value={form.pmUserId || ''}
            onChange={e => updateForm({ pmUserId: e.target.value })}
            className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          >
            <option value="">-- Chọn PM --</option>
            {managers.map(user => (
              <option key={user.id} value={user.id}>{user.full_name}</option>
            ))}
          </select>
        </div>

        {/* Địa điểm (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Địa điểm thực hiện <span className="text-rose-500">*</span></label>
          <input 
            required
            value={form.location || ''}
            onChange={e => updateForm({ location: e.target.value })}
            placeholder="Nhập địa chỉ dự án"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Tỉnh/Thành phố (1 column) */}
        <div className="space-y-1.5">
          <label className="text-xs lg:text-sm font-normal text-slate-700">Tỉnh/Thành phố</label>
          <input 
            value={form.province || ''}
            onChange={e => updateForm({ province: e.target.value })}
            placeholder="Nhập tỉnh/thành phố"
            className="h-10 w-full rounded-md border border-slate-200 px-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all"
          />
        </div>

        {/* Mô tả dự án (Full width) */}
        <div className="space-y-1.5 md:col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-xs lg:text-sm font-normal text-slate-700">Mô tả chi tiết</label>
            <span className="text-xs text-slate-400 font-normal">0/500</span>
          </div>
          <textarea 
            rows={3}
            value={form.description || ''}
            onChange={e => updateForm({ description: e.target.value })}
            placeholder="Nhập mô tả dự án"
            className="w-full rounded-md border border-slate-200 p-3 text-xs lg:text-sm font-normal text-slate-700 outline-none focus:border-emerald-500 transition-all resize-none"
          />
        </div>
      </div>
    </section>
  );
}
